'use client';

import { useCallback, useEffect, useState } from 'react';
import { capture } from '@/lib/analytics';
import type { PluginDownload } from '@/lib/downloads/config';
import { copyDownloadUrl } from '@/lib/downloads/copy-download-url';
import { triggerDownload } from '@/lib/downloads/trigger-download';

export type PluginDownloadOutcome =
  | 'download_started'
  | 'opened_in_browser'
  | 'link_copied'
  | 'copy_failed';

export function usePluginDownloadAction() {
  const [isRhinoHost, setIsRhinoHost] = useState(false);
  const [canOpenExternal, setCanOpenExternal] = useState(false);

  useEffect(() => {
    const updateRhinoHost = () => {
      setIsRhinoHost(Boolean(window.arkyvRhino));
      setCanOpenExternal(typeof window.arkyvRhino?.openExternal === 'function');
    };

    updateRhinoHost();
    window.addEventListener('arkyv-rhino-ready', updateRhinoHost);
    return () => {
      window.removeEventListener('arkyv-rhino-ready', updateRhinoHost);
    };
  }, []);

  const startDownload = useCallback(
    async (download: PluginDownload): Promise<PluginDownloadOutcome> => {
      if (!download.url) {
        return 'copy_failed';
      }

      const rhinoBridge = window.arkyvRhino;
      if (rhinoBridge) {
        if (rhinoBridge.openExternal) {
          rhinoBridge.openExternal(download.url);
          capture(`${download.product}_plugin_downloaded`, {
            version: download.version,
            source: 'downloads_page',
            ...(download.platform ? { platform: download.platform } : {}),
          });
          return 'opened_in_browser';
        }

        return (await copyDownloadUrl(download.url))
          ? 'link_copied'
          : 'copy_failed';
      }

      triggerDownload(download.url, download.filename);
      capture(`${download.product}_plugin_downloaded`, {
        version: download.version,
        source: 'downloads_page',
        ...(download.platform ? { platform: download.platform } : {}),
      });
      return 'download_started';
    },
    [],
  );

  return { canOpenExternal, isRhinoHost, startDownload };
}
